import { apiClient } from '@/api/client';
import {
  Business,
  PathsAdminBusinessesGetParametersQueryStatus,
} from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { mdiCalculator, mdiRefresh, mdiStore } from '@mdi/js';
import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { BusinessesFilters } from './BusinessesFilters';
import { BusinessesTable } from './BusinessesTable';

export interface BusinessesListProps {
  onRowClick: (business: Business) => void;
  onEditBusiness: (business: Business) => void;
  onDeleteBusiness: (business: Business) => void;
  isDeleting: boolean;
}

export const BusinessesList: React.FC<BusinessesListProps> = ({
  onRowClick,
  onEditBusiness,
  onDeleteBusiness,
  isDeleting,
}) => {
  const { addNotification } = useNotifications();

  const recalculateScoresMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.POST('/admin/scores/recalculate-all');
      if (response.error) {
        throw new Error('Failed to trigger score recalculation');
      }
      return response.data;
    },
    onSuccess: data => {
      addNotification({
        type: 'success',
        title: 'Score Recalculation Started',
        message: `${data.queued} businesses queued for score recalculation`,
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to trigger score recalculation',
      });
    },
  });

  const handleRecalculateScores = () => {
    if (
      window.confirm(
        'Are you sure you want to recalculate scores for ALL businesses? This may take a while.',
      )
    ) {
      recalculateScoresMutation.mutate();
    }
  };

  const { state, list, actions, isLoading } = usePaginatedList<
    Business,
    { status: string; categoryId: string; country: string }
  >({
    queryKey: queryKeys.ADMIN_BUSINESSES,
    endpoint: '/admin/businesses',
    initialFilters: { status: '', categoryId: '', country: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      status:
        (filters.status as PathsAdminBusinessesGetParametersQueryStatus) ||
        undefined,
      categoryId: filters.categoryId ? parseInt(filters.categoryId) : undefined,
      country: filters.country || undefined,
      sortBy,
      sortOrder,
    }),
  });

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center'>
            <Icon path={mdiStore} size={32} className='mr-3' />
            Businesses
          </h1>
          <p className='text-gray-600 mt-2'>
            Manage businesses and their information
          </p>
        </div>
        <div className='flex items-center space-x-3'>
          <div className='text-sm text-gray-600'>
            <Icon path={mdiStore} size={16} className='mr-1 inline' />
            {list.total} total businesses
          </div>
          <Button
            icon={mdiCalculator}
            color='warning'
            label='Recalculate Scores'
            title='Recalculate work-friendly scores for all businesses'
            onClick={handleRecalculateScores}
            loading={recalculateScoresMutation.isPending}
            small
          />
          <Button
            icon={mdiRefresh}
            color='light'
            title='Refresh'
            onClick={actions.refetch}
            loading={isLoading}
          />
        </div>
      </div>

      <BusinessesFilters
        searchTerm={state.search}
        selectedStatus={state.filters.status}
        selectedCategory={state.filters.categoryId}
        selectedCountry={state.filters.country}
        onSearchChange={actions.setSearch}
        onStatusChange={value => actions.setFilter('status', value)}
        onCategoryChange={value => actions.setFilter('categoryId', value)}
        onCountryChange={value => actions.setFilter('country', value)}
        onClearFilters={actions.clearFilters}
      />

      <BusinessesTable
        businesses={list.items}
        isLoading={isLoading}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSort={actions.setSort}
        onRowClick={onRowClick}
        onEditBusiness={onEditBusiness}
        onDeleteBusiness={onDeleteBusiness}
        isDeleting={isDeleting}
        selectedCountry={state.filters.country}
      />

      {list.totalPages > 1 && (
        <Pagination
          currentPage={list.currentPage}
          totalPages={list.totalPages}
          onPageChange={actions.setPage}
          totalItems={list.total}
          itemsPerPage={list.limit}
        />
      )}
    </div>
  );
};
